import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import {
  bordeiroService,
  BordeiroResumoResponse,
} from "../../../services/bordeiroService";
import { useAuth } from "../../../context/AuthContext";
import {
  canFinalizarFornoOuBordas,
  hasFullAccessUser,
  isLideresRoleNome,
} from "../../../lib/screenAccess";
import { useBordeiroEvents } from "../../../hooks/useBordeiroEvents";
import { broadcastDashboardRefresh } from "../../../utils/dashboardRefreshBroadcast";
import { lojaService } from "../../../services/lojaService";
import { Loja } from "../../../types/loja";

type HeaderStats = {
  totalPendentes: number;
  totalFeitas: number;
  dataReferencia: string;
};

export type PendingCard =
  | {
      kind: "borda";
      cardId: string;
      bordaNome: string;
      nome: string;
      referencia: {
        orderId: number | null;
        displayId: number | null;
        tamanho: string;
        observation: string | null;
        assignedToUserNome: string | null;
        filaSalgadaId: number | null;
        filaDoceId: number | null;
        filaSalaoSalgadaId: number | null;
        filaSalaoDoceId: number | null;
        bordaPreparoId?: number;
      } | null;
    }
  | {
      kind: "paozinho";
      cardId: string;
      nome: string;
      paozinhoPreparoId: number;
      referencia: {
        orderId: number | null;
        displayId: number | null;
        observation: string | null;
        origem: "cardapio" | "salao";
        salaoMesaId: string | null;
        salaoComandaId: string | null;
        salaoIdentificador: string | null;
      } | null;
    };

interface BordeiroContextType {
  resumo: BordeiroResumoResponse | null;
  loading: boolean;
  refreshing: boolean;
  headerStats: HeaderStats;
  pendingCards: PendingCard[];
  fetchResumo: () => Promise<void>;
  refreshNow: () => Promise<void>;
  handleFinalizar: (card: PendingCard) => void;
  canFinalizar: boolean;
  showLojaSelector: boolean;
  lojaOptions: Array<{ value: number; label: string }>;
  selectedLojaId: number | undefined;
  setSelectedLojaId: (id: number | undefined) => void;
}

const BordeiroContext = createContext<BordeiroContextType | undefined>(
  undefined
);

export const BordeiroProvider = ({ children }: { children: ReactNode }) => {
  const { user } = useAuth();
  const [resumo, setResumo] = useState<BordeiroResumoResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [hiddenCardIds, setHiddenCardIds] = useState<Record<string, true>>({});
  const isFetchingRef = useRef(false);
  const hasLoadedRef = useRef(false);
  const bordasEmFaltaAlertShownRef = useRef<string>("");
  /** Fingerprints já avisados nesta “onda” crítica; sai da lista = pode avisar de novo ao reatingir. */
  const estoqueCriticoAlertedRef = useRef<Set<string>>(new Set());
  const audioBordaEmFaltaRef = useRef<HTMLAudioElement | null>(null);
  const [sseAvailable, setSseAvailable] = useState(true);

  const podeEscolherLoja =
    hasFullAccessUser(user) ||
    user?.roleId === 8 ||
    isLideresRoleNome(user?.role?.nome);
  const canFinalizar = canFinalizarFornoOuBordas(user);

  const [selectedLojaId, setSelectedLojaId] = useState<number | undefined>(
    () => user?.lojaId
  );

  const effectiveLojaId = podeEscolherLoja
    ? (selectedLojaId ?? user?.lojaId)
    : user?.lojaId;

  const { data: lojas = [] } = useQuery({
    queryKey: ["lojas"],
    queryFn: () => lojaService.getLojas(),
    enabled: podeEscolherLoja,
  });

  const lojaOptions = useMemo(
    () =>
      (lojas as Loja[]).map((loja) => ({
        value: loja.id,
        label: loja.nome,
      })),
    [lojas]
  );

  const playBordaEmFaltaSound = useCallback(() => {
    try {
      if (!audioBordaEmFaltaRef.current) {
        audioBordaEmFaltaRef.current = new Audio("/entregas.mp3");
        audioBordaEmFaltaRef.current.volume = 1;
      }
      audioBordaEmFaltaRef.current.currentTime = 0;
      audioBordaEmFaltaRef.current.play().catch(() => {});
    } catch {}
  }, []);

  const fetchResumo = useCallback(async () => {
    if (isFetchingRef.current) return;

    isFetchingRef.current = true;
    try {
      if (!hasLoadedRef.current) setLoading(true);

      const data = await bordeiroService.getResumo(effectiveLojaId);
      setResumo({
        ...data,
        paozinhos: data.paozinhos ?? {
          totalPendentes: 0,
          totalFeitas: 0,
          itens: [],
        },
      });
    } catch (error: any) {
      toast.error(error?.message || "Erro ao carregar quadro de bordas.");
    } finally {
      if (!hasLoadedRef.current) {
        setLoading(false);
        hasLoadedRef.current = true;
      }
      isFetchingRef.current = false;
    }
  }, [effectiveLojaId]);

  useEffect(() => {
    fetchResumo();
  }, [fetchResumo]);

  useEffect(() => {
    setHiddenCardIds({});
    estoqueCriticoAlertedRef.current = new Set();
  }, [effectiveLojaId]);

  useEffect(() => {
    if (sseAvailable) return;

    const interval = setInterval(() => {
      void fetchResumo();
    }, 60_000);

    return () => clearInterval(interval);
  }, [sseAvailable, fetchResumo]);

  const onBordaEmFaltaRealtime = useCallback(
    (bordaNome: string) => {
      playBordaEmFaltaSound();
      Swal.fire({
        title: "Atenção",
        html: `Borda <strong>${bordaNome}</strong> em falta! Finalize ao menos uma unidade para tirar o destaque.`,
        icon: "warning",
        confirmButtonText: "Entendi",
      });
    },
    [playBordaEmFaltaSound]
  );

  useBordeiroEvents(
    fetchResumo,
    effectiveLojaId ?? null,
    onBordaEmFaltaRealtime,
    { onAvailabilityChange: setSseAvailable }
  );

  const refreshNow = useCallback(async () => {
    if (isFetchingRef.current) return;
    setRefreshing(true);
    try {
      await fetchResumo();
    } finally {
      setRefreshing(false);
    }
  }, [fetchResumo]);

  useEffect(() => {
    const bordas = resumo?.bordasEmFalta ?? [];
    if (bordas.length === 0) {
      bordasEmFaltaAlertShownRef.current = "";
      return;
    }
    const key = [...bordas].sort().join(",");
    if (bordasEmFaltaAlertShownRef.current === key) return;
    bordasEmFaltaAlertShownRef.current = key;
    playBordaEmFaltaSound();
    Swal.fire({
      title: "Atenção",
      html: `Há <strong>borda(s) em falta</strong>: ${bordas.join(", ")}. Finalize ao menos uma unidade de cada para tirar o destaque.`,
      icon: "warning",
      confirmButtonText: "Entendi",
    });
  }, [resumo?.bordasEmFalta, playBordaEmFaltaSound]);

  useEffect(() => {
    const itens = resumo?.estoqueCritico ?? [];
    const fingerprint = (item: {
      bordaNome: string;
      tamanho: string;
      planejado: number;
    }) => `${item.bordaNome}|${item.tamanho}|p${item.planejado}`;

    const atuais = new Set(itens.map(fingerprint));

    // Saiu da zona crítica (ou planejado mudou) → libera novo aviso ao reatingir o mínimo
    estoqueCriticoAlertedRef.current.forEach((key) => {
      if (!atuais.has(key)) estoqueCriticoAlertedRef.current.delete(key);
    });

    const novos = itens.filter(
      (item) => !estoqueCriticoAlertedRef.current.has(fingerprint(item)),
    );
    if (novos.length === 0) return;

    for (const item of novos) {
      estoqueCriticoAlertedRef.current.add(fingerprint(item));
    }

    const lista = novos
      .map(
        (i) =>
          `<li><strong>${i.bordaNome}</strong> (${i.tamanho}) — ${i.percentual}% (${i.consumido}/${i.planejado})</li>`,
      )
      .join("");

    playBordaEmFaltaSound();
    Swal.fire({
      title: "Estoque baixo — produza mais",
      html: `O armário atingiu o mínimo (<strong>80%</strong> do planejado). Produza mais destas bordas:<ul style="text-align:left;margin:12px 0 0;padding-left:1.2em">${lista}</ul>`,
      icon: "warning",
      confirmButtonText: "Entendi",
    });
  }, [resumo?.estoqueCritico, playBordaEmFaltaSound]);

  const applyResumo = useCallback((data: BordeiroResumoResponse) => {
    setResumo({
      ...data,
      paozinhos: data.paozinhos ?? {
        totalPendentes: 0,
        totalFeitas: 0,
        itens: [],
      },
    });
  }, []);

  const handleFinalizar = (card: PendingCard) => {
    setHiddenCardIds((prev) => ({ ...prev, [card.cardId]: true }));

    void (async () => {
      try {
        if (card.kind === "paozinho") {
          const data = await bordeiroService.finalizarPaozinho(
            card.paozinhoPreparoId
          );
          applyResumo(data);
          broadcastDashboardRefresh();
          toast.success("Pãozinho enviado ao forno.");
        } else {
          const data = await bordeiroService.finalizarBorda(
            card.bordaNome,
            1,
            {
              orderId: card.referencia?.orderId ?? null,
              displayId: card.referencia?.displayId ?? null,
              tamanho: card.referencia?.tamanho ?? null,
              filaSalgadaId: card.referencia?.filaSalgadaId ?? null,
              filaDoceId: card.referencia?.filaDoceId ?? null,
              filaSalaoSalgadaId: card.referencia?.filaSalaoSalgadaId ?? null,
              filaSalaoDoceId: card.referencia?.filaSalaoDoceId ?? null,
            },
            effectiveLojaId
          );
          applyResumo(data);
          broadcastDashboardRefresh();
          toast.success("Borda contabilizada com sucesso.");
        }
        setHiddenCardIds((prev) => {
          if (!prev[card.cardId]) return prev;
          const next = { ...prev };
          delete next[card.cardId];
          return next;
        });
      } catch (error: any) {
        setHiddenCardIds((prev) => {
          if (!prev[card.cardId]) return prev;
          const next = { ...prev };
          delete next[card.cardId];
          return next;
        });
        toast.error(
          error?.message ||
            (card.kind === "paozinho"
              ? "Erro ao finalizar pãozinho."
              : "Erro ao finalizar borda.")
        );
        void fetchResumo();
      }
    })();
  };

  const headerStats = useMemo<HeaderStats>(() => {
    if (!resumo) {
      return {
        totalPendentes: 0,
        totalFeitas: 0,
        dataReferencia: "--/--/----",
      };
    }

    const [year, month, day] = resumo.dataReferencia.split("-");
    return {
      totalPendentes: resumo.totalPendentes,
      totalFeitas: resumo.totalFeitas,
      dataReferencia: `${day}/${month}/${year}`,
    };
  }, [resumo]);

  const pendingCards = useMemo<PendingCard[]>(() => {
    if (!resumo) return [];

    const cardsBorda: PendingCard[] = resumo.itens.flatMap((item) =>
      Array.from({ length: item.pendentes }, (_, index) => {
        const referencia = item.referenciasPendentes[index] || null;
        return {
          kind: "borda" as const,
          cardId: referencia?.bordaPreparoId
            ? `borda-${referencia.bordaPreparoId}`
            : `borda-${item.bordaNome}-${referencia?.filaSalgadaId ?? ""}-${referencia?.filaDoceId ?? ""}-${referencia?.filaSalaoSalgadaId ?? ""}-${referencia?.filaSalaoDoceId ?? ""}-${index}-${referencia?.displayId ?? ""}`,
          bordaNome: item.bordaNome,
          nome: item.nome,
          referencia,
        };
      })
    );

    const pzItens = resumo.paozinhos?.itens ?? [];
    const cardsPaozinho: PendingCard[] = [];
    for (const item of pzItens) {
      for (let index = 0; index < item.pendentes; index++) {
        const ref = item.referenciasPendentes[index];
        if (!ref?.paozinhoPreparoId) continue;
        cardsPaozinho.push({
          kind: "paozinho",
          cardId: `pz-${ref.paozinhoPreparoId}-${index}`,
          nome: item.nome,
          paozinhoPreparoId: ref.paozinhoPreparoId,
          referencia: {
            orderId: ref.orderId,
            displayId: ref.displayId,
            observation: ref.observation,
            origem: ref.origem ?? "cardapio",
            salaoMesaId: ref.salaoMesaId ?? null,
            salaoComandaId: ref.salaoComandaId ?? null,
            salaoIdentificador: ref.salaoIdentificador ?? null,
          },
        });
      }
    }

    return [...cardsBorda, ...cardsPaozinho]
      .filter((c) => !hiddenCardIds[c.cardId])
      .sort((a, b) => {
        const dA = a.referencia?.displayId ?? Infinity;
        const dB = b.referencia?.displayId ?? Infinity;
        if (dA !== dB) return dA - dB;
        const oA = a.referencia?.orderId ?? Infinity;
        const oB = b.referencia?.orderId ?? Infinity;
        return oA - oB;
      });
  }, [resumo, hiddenCardIds]);

  return (
    <BordeiroContext.Provider
      value={{
        resumo,
        loading,
        refreshing,
        headerStats,
        pendingCards,
        fetchResumo,
        refreshNow,
        handleFinalizar,
        canFinalizar,
        showLojaSelector: podeEscolherLoja,
        lojaOptions,
        selectedLojaId,
        setSelectedLojaId,
      }}
    >
      {children}
    </BordeiroContext.Provider>
  );
};

export const useBordeiro = () => {
  const context = useContext(BordeiroContext);
  if (!context) {
    throw new Error("useBordeiro must be used within BordeiroProvider");
  }
  return context;
};
